import { motion } from "framer-motion";
import { MANIFESTO } from "@/data/content";

const EASE = [0.16, 1, 0.3, 1];

export default function Manifesto() {
  return (
    <section
      id="manifesto"
      className="relative overflow-hidden border-t border-amber-500/10 bg-coal px-6 py-24 sm:px-10 lg:px-16 lg:py-32"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-40 top-0 h-[500px] w-[500px] rounded-full bg-ember/15 blur-[140px]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-40 bottom-0 h-[500px] w-[500px] rounded-full bg-crimson/15 blur-[140px]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-1/2 h-[400px] w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold/10 blur-[160px]"
      />

      <div className="relative grid gap-14 lg:grid-cols-2 lg:gap-20">
        <div className="lg:sticky lg:top-28 lg:self-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE }}
          >
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.35em] text-gold">
              The Manifesto
            </p>
            <h2 className="bg-gradient-to-r from-amber-100 via-gold to-ember bg-clip-text font-serif text-2xl uppercase tracking-tight text-transparent sm:text-3xl lg:text-4xl">
              Why Fire Still Matters
            </h2>
            <p className="mt-6 max-w-md text-base font-light leading-relaxed text-neutral-300 sm:text-lg">
              {MANIFESTO.intro}
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
            className="group relative mt-10"
          >
            <div
              aria-hidden="true"
              className="absolute -inset-6 bg-gradient-to-tr from-ember/25 via-gold/15 to-crimson/20 opacity-60 blur-2xl transition-opacity duration-700 group-hover:opacity-100"
            />
            <img
              src={MANIFESTO.portrait}
              alt={MANIFESTO.portraitAlt}
              loading="lazy"
              className="relative aspect-[4/5] w-full border border-ember/30 object-cover brightness-[1.1] contrast-[1.08] saturate-[1.45] transition-transform duration-700 ease-out group-hover:scale-[1.02]"
              data-testid="manifesto-portrait"
            />
            <p className="relative mt-8 font-mono text-xs uppercase tracking-[0.3em] text-gold/70">
              Chef at the hearth — every service, every flame
            </p>
          </motion.div>
        </div>

        <div className="flex flex-col justify-center">
          {MANIFESTO.chapters.map((ch, i) => (
            <motion.div
              key={ch.number}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: i * 0.1, ease: EASE }}
              className="group border-b border-amber-500/15 py-10 first:pt-0 last:border-b-0"
              data-testid={`manifesto-chapter-${ch.number}`}
            >
              <div
                aria-hidden="true"
                data-testid={`manifesto-chapter-emblem-${ch.number}`}
                className="h-9 w-9 transition-transform duration-500 group-hover:-translate-y-1 group-hover:scale-110 sm:h-11 sm:w-11"
                style={{
                  background: "linear-gradient(135deg, #E5A93C 0%, #FF7A00 50%, #B80D36 100%)",
                  WebkitMaskImage: "url(/emblem-light.png)",
                  maskImage: "url(/emblem-light.png)",
                  WebkitMaskSize: "contain",
                  maskSize: "contain",
                  WebkitMaskRepeat: "no-repeat",
                  maskRepeat: "no-repeat",
                  WebkitMaskPosition: "center",
                  maskPosition: "center",
                }}
              />
              <div className="mt-4 h-px w-12 bg-gradient-to-r from-ember to-gold transition-all duration-500 group-hover:w-24" />
              <h3 className="mt-4 font-serif text-xl font-medium text-amber-100 sm:text-2xl">
                {ch.title}
              </h3>
              <p className="mt-4 max-w-lg text-sm leading-relaxed text-neutral-300 sm:text-base">
                {ch.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
